
import React, {
  Fragment, useContext, useCallback, useEffect, useState,
} from 'react';
import classNames from 'classnames';
import i18next from 'i18next';
import { LayoutContext } from 'lib/ContextTypes/layout';

import { Button } from 'components/Button';
import { PackageTitleTopBorder } from 'components/PackageTitleTopBorder';

import PackageTitle from 'components/PackageTitle';
import WideTease from 'components/WideTease';

import Ad from 'components/Ad';
import styles from './styles.module.scss';

const POSSIBLE_METADATA = {
  ALLTEXT: 'seeAllText',
  ALLURL: 'seeAllUrl',
  BRAND: 'showBrand',
  DEK: 'showDek',
  EYEBROW: 'showEyebrow',
  LOADMORE: 'showLoadMore',
  LOGO: 'logoUrl',
  TEASE: 'showTeaseImage',
  TIMESTAMP: 'showTimestamp',
  TITLE: 'title',
};

const ITEMS_PER_PAGE = 20;
const INITIAL_NUMBER_ITEMS = 11;

/**
 * Feeds component - Renders a paginated feed of content items with automatic ad insertion
 * @param {object} root0 - Component props
 * @param {string} root0.additionalClasses - Additional CSS classes to apply
 * @param {object} root0.content - Content object containing items and metadata
 * @param {boolean} root0.isRail - Whether the feed is displayed in a rail layout
 * @param {string} root0.pkgClassName - Package CSS class name
 * @param {string} root0.vertical - Vertical identifier for the feed
 * @param {boolean} root0.isFullWidth - Whether the feed should display full width
 */
function Feeds({
  additionalClasses = '',
  content = {},
  isRail = false,
  pkgClassName = 'pkg feeds',
  vertical,
  isFullWidth = false,
}) {
  const { isFluidWidthPage } = useContext(LayoutContext);

  const [numberOfItems, setNumberOfItems] = useState(INITIAL_NUMBER_ITEMS);

  /**
   * Get metadata value from content object with optional default
   * @param {string} path - Path to metadata property
   * @param {*} defaultValue - Default value if metadata not found (defaults to true)
   * @returns {*} Metadata value or default
   */
  const getMetadata = useCallback((path, defaultValue) => content.metadata?.[path] ?? (typeof defaultValue !== 'undefined' ? defaultValue : true), [content.metadata]);

  useEffect(() => {
    const loadMore = getMetadata([POSSIBLE_METADATA.LOADMORE]);
    let itemsCount = INITIAL_NUMBER_ITEMS;
    if (!loadMore || itemsCount >= content.items.length) {
      itemsCount = content.items.length;
    }
    setNumberOfItems(itemsCount);
  }, [content.items.length, getMetadata]);

  /**
   * Check if all items are currently displayed
   * @returns {boolean} True if showing all items, false otherwise
   */
  const isLastPage = () => numberOfItems >= content.items.length;

  /**
   * Handle load more button click to display additional items
   */
  const onClickLoadMore = () => {
    let itemsToShow = numberOfItems + ITEMS_PER_PAGE;
    if (itemsToShow >= content.items.length) {
      itemsToShow = content.items.length;
    }
    setNumberOfItems(itemsToShow);
  };

  /**
   * Render inline advertisement component with responsive layouts
   * @returns {React.ReactElement} Ad component with desktop and mobile variants
   */
  const renderInlineAd = () => (
    <Fragment key={content.id}>
      <div className="dn db-m">
        <Ad
          key={content.id}
          packageId={content.id}
          content={content}
          additionalClasses={additionalClasses}
          isRail={isRail}
          pkgClassName="pkg-ad ad ad--centered"
          vertical={vertical}
          isFullWidth={isFullWidth}
          slot="topbanner"
          offsetViewport={100}
          refreshInterval={0}
        />
      </div>
      <div className="dn-m">
        <Ad
          key={content.id}
          packageId={content.id}
          content={content}
          additionalClasses={additionalClasses}
          isRail={isRail}
          pkgClassName="pkg-ad ad ad--centered"
          vertical={vertical}
          isFullWidth={isFullWidth}
          slot="boxinline"
          offsetViewport={50}
          refreshInterval={0}
        />
      </div>
    </Fragment>
  );

  /**
   * Render a single article item in the feed
   * @param {object} article - Article content object
   * @param {number} index - Index of the article in the list
   * @param {boolean} isLastArticle - Whether this is the last article in the feed
   * @returns {React.ReactElement|null} WideTease component or null if invalid
   */
  const renderArticleFeedsListItemContainer = (article, index, isLastArticle) => {
    if (!article) {
      return null;
    }
    const { id, computedValues, metadata: contentMetadata } = article;
    const { metadata: packageMetadata } = content;
    const title = packageMetadata?.title;
    const hasPackageTitle = !!title;
    if (!computedValues) {
      return null;
    }
    const metadata = {
      ...packageMetadata,
      ...contentMetadata,
    };
    return (
      <WideTease
        key={id}
        hideBorder={!hasPackageTitle && index === 0}
        content={{ ...article, metadata }}
        isLastArticle={isLastArticle}
        vertical={vertical}
        isRail={isRail}
      />
    );
  };

  /**
   * Insert ads into a feed list at position 5, 15, 25, 35, etc.
   * @param {Array} items - Array of content items
   * @returns {Array} Array with ad objects inserted at specified positions
   */
  const insertInlineAdsIntoFeedsList = (items) => items
    .map((item, index) => {
      const idx = index + 1;
      return (idx / 5) % 2 === 1 && idx % 5 === 0
        ? [item, { type: 'Ad', id: `Ad-${idx}` }]
        : item;
    })
    .flat();

  /**
   * Render the complete list of feed items with inserted ads
   * @returns {React.ReactElement} Container with all feed items and ads
   */
  const renderFeedsListItems = () => {
    const { items } = content;
    const itemsSliced = items.slice(0, numberOfItems);
    const itemsWithAds = insertInlineAdsIntoFeedsList(itemsSliced);
    return (
      <div className={styles.itemsContainer}>
        {itemsWithAds.map((item, index) => {
          if (isFluidWidthPage && isFullWidth && item.type === 'Ad') {
            return <Fragment key={item.id}>{renderInlineAd()}</Fragment>;
          }
          const articleIndex = itemsSliced.findIndex((i) => i.id === item.id);
          return renderArticleFeedsListItemContainer(
            item,
            index,
            isLastPage() && articleIndex === itemsSliced.length - 1,
          );
        })}
      </div>
    );
  };

  /**
   * Render the package title component based on page layout type
   * @returns {React.ReactElement} PackageTitle or PackageTitleTopBorder component
   */
  const renderPackageTitle = () => {
    const title = getMetadata(POSSIBLE_METADATA.TITLE, null);
    const loadMore = getMetadata(POSSIBLE_METADATA.LOADMORE, true);
    // hide see all if load more
    const seeAllText = loadMore ? null : getMetadata(POSSIBLE_METADATA.ALLTEXT, null);
    const seeAllUrl = loadMore ? null : getMetadata(POSSIBLE_METADATA.ALLURL, null);
    const logoUrl = getMetadata(POSSIBLE_METADATA.LOGO, null);

    return (
      isFluidWidthPage ? (
        <PackageTitle
          metadata={{ title }}
        />
      ) : (
        <PackageTitleTopBorder
          title={title}
          seeAllText={seeAllText}
          seeAllUrl={seeAllUrl}
          logoUrl={logoUrl}
        />
      )
    );
  };

  const loadMore = getMetadata([POSSIBLE_METADATA.LOADMORE]);
  const showLoadButton = !isLastPage() && loadMore;

  return (
    <section
      data-packageid={content.id}
      className={classNames('feeds', pkgClassName, additionalClasses)}
    >
      <div>
        {renderPackageTitle()}
        {renderFeedsListItems()}

        {showLoadButton && (
          <div className={classNames(
            styles.loadMoreWrapper,
            {
              [styles.noTimestamp]: !getMetadata([POSSIBLE_METADATA.TIMESTAMP]),
            },
          )}
          >
            <Button
              type="button-tilt"
              displayType="no-arrow"
              additionalClasses={styles.button}
              onClick={onClickLoadMore}
              title={i18next.t('Load More')}
              size="normal"
              isFullWidth
            />
          </div>
        )}
      </div>
    </section>
  );
}

export { Feeds };
